import { padStart } from 'lodash';
import { mod } from '../utils';

const DEGREES_TO_RADIANS = 2 * Math.PI / 360;

function toRadians(degrees: number) {
    return degrees * DEGREES_TO_RADIANS;
}

function toDegrees(radians: number) {
    return radians / DEGREES_TO_RADIANS;
}

/**
 * Returns the (initial) bearing from ‘this’ point to destination point.
 *
 * @param   {LatLon} point - Latitude/longitude of destination point.
 * @returns {number} Initial bearing in degrees from north.
 *
 * @example
 *     var p1 = new LatLon(52.205, 0.119);
 *     var p2 = new LatLon(48.857, 2.351);
 *     var b1 = p1.bearingTo(p2); // 156.2°
 */
// Modified from here: https://www.movable-type.co.uk/scripts/latlong.html
export function getBearing(fromLngLat: number[], toLngLat: number[]) {
    // tanθ = sinΔλ⋅cosφ2 / cosφ1⋅sinφ2 − sinφ1⋅cosφ2⋅cosΔλ
    // see mathforum.org/library/drmath/view/55417.html for derivation

    const φ1 = toRadians(fromLngLat[1]);
    const φ2 = toRadians(toLngLat[1]);
    const Δλ = toRadians(toLngLat[0] - fromLngLat[0]);
    const y = Math.sin(Δλ) * Math.cos(φ2);
    const x =
        Math.cos(φ1) * Math.sin(φ2) -
        Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
    const θ = Math.atan2(y, x);

    return mod(toDegrees(θ) + 360, 360);
}

/** Interpolates between two points, with any number of dimensions */
export function mixArrays(
    pos1: number[],
    pos2: number[],
    proportion: number
): number[] {
    if (pos1.length !== pos2.length) {
        throw new Error('Lengths of arrays do not match');
    }
    if (proportion >= 1) {
        return [...pos2];
    }
    if (proportion <= 0) {
        return [...pos1];
    }

    const result = [];
    for (let i = 0; i < pos1.length; i += 1) {
        result[i] = pos1[i] + (pos2[i] - pos1[i]) * proportion;
    }
    return result;
}

/** Interpolates between two values using a proportion */
export const mix = (value1: number, value2: number, proportion: number) =>
    value1 + (value2 - value1) * proportion;

export const getDurationParts = (totalSeconds: number) => {
    const totalMinutes = totalSeconds / 60;
    const totalHours = totalMinutes / 60;

    return {
        hours: Math.floor(totalHours),
        minutes: Math.floor(totalMinutes % 60),
        seconds: Math.round(totalSeconds % 60)
    };
};

export const getDurationPartsForDisplay = (
    totalSeconds: number,
    padMinutesAndSeconds: boolean = true
) => {
    const { hours, minutes, seconds } = getDurationParts(totalSeconds);

    const pad = (value: number) =>
        padMinutesAndSeconds
            ? padStart(value.toString(), 2, '0')
            : value.toString();

    return {
        hours: hours ? hours.toString() : '',
        minutes: pad(minutes),
        seconds: pad(seconds)
    };
};

/** Helper to get a text representation of minutes and seconds from a number in minutes */
export const getMinutesAndSecondsText = (totalSeconds: number) => {
    const { hours, minutes, seconds } = getDurationPartsForDisplay(
        totalSeconds
    );
    return `${hours + (hours ? ':' : '')}${minutes}:${seconds}`;
};

/** Gets the time or distance value from a data stream at a specified index and offset */
export const getXAxisValue = (
    xAxis: number[],
    selectedIndex: number,
    selectedIndexOffset: number
) => {
    const pathLength = xAxis.length;
    if (!pathLength) {
        throw new Error('No x axis data provided');
    }
    if (selectedIndex >= pathLength || selectedIndex < 0) {
        throw new Error('Selected index out of range of X axis');
    }

    const value = xAxis[selectedIndex];
    if (!selectedIndexOffset || selectedIndex === pathLength - 1) {
        return value;
    }

    // If an offset is specified, interpolate between the current value and the next value
    const nextValue = xAxis[selectedIndex + 1];
    return mix(value, nextValue, selectedIndexOffset);
};

// /**
//  * Performs a binary search on the host array. This method can either be
//  * injected into Array.prototype or called with a specified scope like this:
//  * binaryIndexOf.call(someArray, searchElement);
//  *
//  * @param {*} searchElement The item to search for within the array.
//  * @return {Number} The index of the element which defaults to -1 when not found.
//  */
// function binaryIndexOf(this: number[], searchElement: number) {

//     let minIndex = 0;
//     let maxIndex = this.length - 1;
//     let currentIndex: number;
//     let currentElement: number;

//     while (minIndex <= maxIndex) {
//         // tslint:disable-next-line:no-bitwise
//         currentIndex = (minIndex + maxIndex) / 2 | 0;
//         currentElement = this[currentIndex];

//         if (currentElement < searchElement) {
//             minIndex = currentIndex + 1;
//         }
//         else if (currentElement > searchElement) {
//             maxIndex = currentIndex - 1;
//         }
//         else {
//             return currentIndex;
//         }
//     }

//     return -1;
// }

/**
 * Gets the selected index for a provided value of distance or time
 * @param xAxis Distance or time data stream
 * @param value Value of distance or time to select
 * @param startFromIndex Index to start searching from, to reduce the number of iterations
 */
export const getXAxisSelectedIndex = (
    xAxis: number[],
    value: number,
    startFromIndex: number = 0
): {
    selectedIndex: number;
    selectedIndexOffset: number;
} => {
    const pathLength = xAxis.length;
    const startIndex =
        xAxis[startFromIndex] > value || value > xAxis[pathLength - 1]
            ? 0
            : startFromIndex;

    let i = 0;
    // tslint:disable-next-line:prefer-for-of
    for (i = 0; i < pathLength; i += 1) {
        // Start from the currently selected index to minimise the number
        // of iterations
        const nextIndex = (i + startIndex) % pathLength;
        const nextValue = xAxis[nextIndex];

        // Get the first index which is greater than the requested distance
        if (nextValue >= value) {
            const prevIndex = Math.max(nextIndex - 1, 0);
            const prevValue = xAxis[prevIndex];
            const offset =
                nextValue - prevValue > 0
                    ? (value - prevValue) / (nextValue - prevValue)
                    : 0;

            return {
                selectedIndex: prevIndex,
                selectedIndexOffset: offset
            };
        }
    }

    return { selectedIndex: 0, selectedIndexOffset: 0 };
};



// WEBPACK FOOTER //
// ./src/client/components/Strava/utils.ts